import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout';
import { useLanguage } from '@/hooks';
import { navigate, useRoute } from '@/lib/router';
import { type ClinicUser, inClinic } from '@/types/api';
import { LoginPage, useAuth } from './features/auth';
import { CalendarFilter, NextPatient } from './features/calendar';
import { ChartPage } from './features/chart';
import { DoctorProvider, useDoctor } from './features/doctors';
import { HomePage } from './features/home';
import { LandingPage } from './features/landing';
import { OfferPage, OffersPage } from './features/offers';
import { PlatformApp } from './features/platform';
import { ProfilePage } from './features/profile';
import { ReportsPage } from './features/reports';
import { SettingsPage } from './features/settings';
import { useOutboxSync } from './features/sync/useOutboxSync';
import { isInstalledApp } from './utils/browser';

function Screen() {
  const route = useRoute();
  switch (route.name) {
    case 'patient':
      return <ChartPage key={route.id} patientId={route.id} />;
    case 'offers':
      return <OffersPage />;
    case 'offer':
      return <OfferPage key={route.id} id={route.id} />;
    case 'reports':
      return <ReportsPage />;
    case 'settings':
      return <SettingsPage />;
    case 'profile':
      return <ProfilePage />;
    default:
      return <HomePage />;
  }
}

function SignedIn({ user }: { user: ClinicUser }) {
  const { t } = useTranslation();
  const { logout, changeLanguage } = useAuth();
  const outbox = useOutboxSync(user.id);
  const { doctors, doctorId, setDoctorId, filter } = useDoctor();
  const route = useRoute();
  const home = route.name === 'patients';
  // Already signed in: /login has nothing to show.
  useEffect(() => {
    if (route.name === 'login') navigate({ name: 'patients' }, true);
  }, [route.name]);

  return (
    <AppShell
      user={user}
      roleLabel={t(`common.role.${user.role}`)}
      outbox={outbox}
      doctors={doctors}
      doctorId={doctorId}
      onDoctor={setDoctorId}
      onLogout={logout}
      onLanguage={changeLanguage}
      center={
        <>
          {/* The calendar and its filter are only on the home screen. */}
          {home && <CalendarFilter className="topbar__filter" />}
          {/* The doctor picked in sight: the filter's at home, else (and with all doctors) the one recorded for. */}
          <NextPatient doctorId={(home && filter) || doctorId} />
        </>
      }
    >
      <Screen />
    </AppShell>
  );
}

/**
 * The signed-in app is keyed on the language, so a switch remounts it: some
 * labels come from helpers that call i18n.t outside React (dental terms,
 * prognosis, error messages) and memoised parts would keep the old language.
 * The login page only uses `t` and re-renders in place, so typed credentials
 * survive a switch.
 */
export default function App() {
  const lang = useLanguage();
  const { user, expired } = useAuth();
  const route = useRoute();
  if (!user) {
    // The start page is only for "/" in a browser. Deep links (/patients/…),
    // /login, an expired session and the installed app open sign-in, which
    // keeps the URL so the user lands where the link pointed.
    const signIn = route.name !== 'patients' || expired || isInstalledApp();
    // Keyed on the language for the same reason as SignedIn (memoised i18n.t calls).
    return signIn ? <LoginPage /> : <LandingPage key={lang} />;
  }
  // A platform administrator: the console, or a clinic's settings in support mode.
  if (user.platform) return <PlatformApp key={lang} user={user} />;
  if (!inClinic(user)) return <LoginPage />;
  return (
    <DoctorProvider user={user}>
      <SignedIn key={lang} user={user} />
    </DoctorProvider>
  );
}
